import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { apiConstant } from 'src/app/app.constant';
import { AlertModal } from 'src/app/app.model';
import { JobAssignmentService } from 'src/app/services/job-assignment.service';
import { JobService } from 'src/app/services/job.service';
import { TimesheetService } from 'src/app/services/timesheet.service';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';

@Component({
  selector: 'app-create-job-assignment',
  templateUrl: './create-job-assignment.component.html',
  styleUrls: ['./create-job-assignment.component.css'],
})
export class CreateJobAssignmentComponent implements OnInit {
  createJobAssignment: FormGroup | any;
  jobs: any = [];
  candidates: any = [];
  error: any = '';
  loading: boolean = false
  searchingJob: boolean = false
  searchingCandidate: boolean = false
  jobSearch: string = ''
  candidateSearch: string = ''
  formControls: any = {};
  timesheetApprovers: any = [];

  approverSearch: string = '';
  approverDropdownOpen: boolean = false;

  @ViewChild('approverInput') approverInputRef: ElementRef<HTMLInputElement>;

  public API_CONSTANT = apiConstant

  constructor(
    private jobService: JobService,
    private timesheetService: TimesheetService,
    private jobAssignmentService: JobAssignmentService,
    public datePipe: DatePipe,
    private activeModalService: NgbActiveModal,
    private modalService: NgbModal,
  ) { }

  ngOnInit(): void {
    this.createJobAssignment = new FormGroup({
      job_uuid: new FormControl('', Validators.required),
      candidate_uuid: new FormControl('', Validators.required),
      start_date: new FormControl('', Validators.required),
      end_date: new FormControl('', Validators.required),
      timesheet_approvers: new FormControl([], Validators.required),
    });
  }

  onSearchChange(event: any) {
    this.createJobAssignment.patchValue({
      [event.formControlName]: event.value.uuid
    })
    if (event.formControlName === 'job_uuid') {
      this.jobs = event.dataArray
    }
    if(event.value && event.value.client_uuid != undefined) {
      this.timesheetApprovers = [];
      this.createJobAssignment.get('timesheet_approvers')?.setValue([]);
      this.getTimesheetApprovers(event.value.client_uuid);
    }
  }

  getTimesheetApprovers(clientUuid: string) {
    this.jobAssignmentService.getTimesheeetApprovers(clientUuid).subscribe(res => {
      this.timesheetApprovers = res.data;
      this.loading = false;
    }, err => {
      this.loading = false;
      console.log(err);
    });
  }

  get availableApprovers(): { uuid: string; name: string }[] {
    return this.timesheetApprovers?.approvers ?? [];
  }

  get selectedApproverUuids(): string[] {
    return this.createJobAssignment?.get('timesheet_approvers')?.value || [];
  }

  get filteredApprovers(): { uuid: string; name: string }[] {
    const term = (this.approverSearch || '').trim().toLowerCase();
    const selected = new Set(this.selectedApproverUuids);
    return this.availableApprovers
      .filter(a => !selected.has(a.uuid))
      .filter(a => !term || (a.name || '').toLowerCase().includes(term));
  }

  getApproverName(uuid: string): string {
    return this.availableApprovers.find(a => a.uuid === uuid)?.name || uuid;
  }

  getApproverInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  }

  addApprover(uuid: string, evt?: Event) {
    if (evt) evt.preventDefault();
    const current: string[] = this.selectedApproverUuids.slice();
    if (!current.includes(uuid)) {
      current.push(uuid);
      this.createJobAssignment.get('timesheet_approvers')?.setValue(current);
    }
    this.approverSearch = '';
    setTimeout(() => this.approverInputRef?.nativeElement?.focus(), 0);
  }

  removeApprover(uuid: string, evt?: Event) {
    if (evt) evt.stopPropagation();
    const current: string[] = this.selectedApproverUuids.filter((u: string) => u !== uuid);
    this.createJobAssignment.get('timesheet_approvers')?.setValue(current);
  }

  focusApproverInput(evt: Event) {
    const target = evt.target as HTMLElement;
    if (target.closest('.chip') || target.closest('.chip-dropdown')) return;
    this.approverInputRef?.nativeElement?.focus();
  }

  onApproverBlur() {
    setTimeout(() => { this.approverDropdownOpen = false; }, 150);
  }

  get minLimit() {
    if (this.createJobAssignment?.value?.job_uuid) {
      const job = this.jobs?.find((x: any) => x.uuid === this.createJobAssignment?.value?.job_uuid);
      if (job) {
        return job?.start_date
      } else {
        return this.datePipe.transform(new Date(), 'yyyy-MM-dd');
      }
    } else {
      return this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    }
  }

  get maxLimit() {
    if (this.createJobAssignment?.value?.job_uuid) {
      const job = this.jobs?.find((x: any) => x.uuid === this.createJobAssignment?.value?.job_uuid);
      if (job) {
        return job?.end_date
      } else {
        return this.datePipe.transform(new Date(), 'yyyy-MM-dd');
      }
    } else {
      return this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    }
  }

  get hasValue() {
    let hasValue = false;
    Object.keys(this.createJobAssignment.value).forEach(element => {
      const val = this.createJobAssignment.value[element];
      if (Array.isArray(val) ? val.length > 0 : val) {
        hasValue = true;
      }
    });
    return hasValue
  }

  onClose() {
    if (!this.hasValue) {
      this.activeModalService.close();
      return
    }
    const modalRef = this.modalService.open(AlertModalComponent, {
      size: 'md',
      modalDialogClass: 'alert-modal modal-dialog-centered',
      backdrop: false,
    });
    let data: AlertModal = {
      message: 'Are you sure you want to discard Job Assignment Creation?',
      yesAction: 'Discard',
      noAction: 'Cancel',
      yesActionColor: '#0566EA',
      noActionColor: 'transparent',
    }
    modalRef.componentInstance.data = data;
    modalRef.result.then((data) => {
      if (data?.action === 'yes') {
        this.activeModalService.close();
      }
    });
  }

  onSubmit() {
    this.loading = true;
    const data = {
      job_uuid: this.createJobAssignment.value.job_uuid,
      candidate_uuid: this.createJobAssignment.value.candidate_uuid,
      start_date: this.datePipe.transform(this.createJobAssignment.value.start_date, 'YYYY-MM-dd'),
      end_date: this.datePipe.transform(this.createJobAssignment.value.end_date, 'YYYY-MM-dd'),
      timesheet_approvers: this.createJobAssignment.value.timesheet_approvers,
    };
    this.jobAssignmentService.addJobAssignments(data).subscribe((res) => {
      this.loading = false
      this.activeModalService.close({ refresh: true });
    }, error => {
      this.loading = false
      this.error = error;
    }
    );
  }

}
